import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Flame, Send, Loader2, Lock, Sparkles, HelpCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatDate(iso, lng) {
  try {
    return new Date(iso).toLocaleDateString(lng || "fr-FR", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

export default function Campfire() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const unlimited = !!(user && user.unlimited);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [preview, setPreview] = useState([]);
  const [content, setContent] = useState("");
  const [type, setType] = useState("epiphany");
  const [posting, setPosting] = useState(false);
  const [regCount, setRegCount] = useState(null);
  const taRef = useRef(null);
  const regLabel = { fr: "inscrits au feu de camp", en: "campfire sign-ups", es: "inscritos al fuego de campamento", nl: "kampvuur-inschrijvingen" }[(i18n.language || "fr").slice(0, 2)] || "inscrits au feu de camp";
  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setRegCount(data?.campfire_registrations ?? null)).catch(() => {});
  }, []);
  const RegBadge = () => (regCount == null ? null : (
    <div className="flex justify-center mb-10" data-testid="campfire-registrations-count">
      <span className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 border border-primary/25 text-sm text-foreground/70">
        <Flame size={14} className="text-primary/80" />
        <span className="font-display text-gradient-orange tabular-nums">{new Intl.NumberFormat(i18n.language).format(regCount)}</span>
        {regLabel}
      </span>
    </div>
  ));

  const load = async () => {
    try {
      const { data } = await api.get("/campfire");
      setPosts(data);
      setLocked(false);
    } catch (e) {
      if (e.response?.status === 402) {
        setLocked(true);
        try {
          const { data } = await api.get("/campfire/preview");
          setPreview(data);
        } catch { /* ignore */ }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const post = async () => {
    const value = content.trim();
    if (value.length < 3) { toast.error(t("atelier.campfireTooShort")); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/campfire", { content: value, type });
      setPosts((p) => [data, ...p]);
      setContent("");
      toast.success(t("atelier.campfirePosted"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.silent"));
    } finally {
      setPosting(false);
    }
  };

  const echo = async (id) => {
    setPosts((p) => p.map((x) => x.id === id
      ? { ...x, echoed: !x.echoed, echoes: x.echoes + (x.echoed ? -1 : 1) } : x));
    try {
      const { data } = await api.post(`/campfire/${id}/echo`);
      setPosts((p) => p.map((x) => x.id === id ? { ...x, echoed: data.echoed, echoes: data.echoes } : x));
    } catch { load(); }
  };

  const remove = async (id) => {
    setPosts((p) => p.filter((x) => x.id !== id));
    try {
      await api.delete(`/campfire/${id}`);
      toast.success(t("atelier.campfireDeleted"));
    } catch { load(); }
  };

  if (locked) {
    return (
      <div className="h-full overflow-y-auto scroll-thin" data-testid="campfire-locked">
        <div className="max-w-xl mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Lock className="text-primary" size={26} />
          </div>
          <h2 className="font-display text-gradient-orange text-3xl sm:text-4xl italic mb-4">{t("atelier.campfire")}</h2>
          <p className="text-foreground/60 leading-relaxed mb-3">{t("atelier.campfireLocked")}</p>
          <p className="text-foreground/45 leading-relaxed mb-8">{t("atelier.campfireLockedDesc")}</p>
          <RegBadge />

          {/* blurred community preview */}
          {preview.length > 0 && (
            <div className="relative mb-8" data-testid="campfire-preview">
              <div className="space-y-3 select-none pointer-events-none blur-[6px] opacity-80">
                {preview.map((p) => (
                  <div key={p.id} className="glass rounded-2xl p-4 text-left">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="font-display text-foreground/85 text-sm">{p.user_name}</span>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                        {p.type === "question" ? t("atelier.campfireQuestion") : t("atelier.campfireEpiphany")}
                      </span>
                    </div>
                    <p className="font-display italic text-foreground/75 leading-relaxed">{p.content}</p>
                    <p className="text-xs text-primary/70 mt-2 flex items-center gap-1"><Flame size={12} /> {p.echoes}</p>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-background via-background/40 to-transparent">
                <span className="text-xs uppercase tracking-[0.2em] text-foreground/50 mb-2">{t("atelier.campfirePreviewCta")}</span>
              </div>
            </div>
          )}

          <button
            onClick={() => navigate("/tarifs")}
            data-testid="campfire-upgrade-btn"
            className="btn-frost-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm"
          >
            <Flame size={16} /> {t("atelier.upgrade")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="campfire-tab">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-4 text-center">le cercle</p>
        <h2 className="font-display text-gradient-orange text-center text-4xl sm:text-5xl italic mb-3">{t("atelier.campfire")}</h2>
        <p className="text-foreground/50 max-w-lg mx-auto text-center mb-10 leading-relaxed">{t("atelier.campfireSubtitle")}</p>
        <RegBadge />

        {/* composer */}
        <div className="glass frame-pulse rounded-2xl p-5 mb-10">
          <div className="flex gap-2 mb-3">
            {[["epiphany", Sparkles, t("atelier.campfireEpiphany")], ["question", HelpCircle, t("atelier.campfireQuestion")]].map(([val, Icon, label]) => (
              <button
                key={val}
                data-testid={`campfire-type-${val}`}
                onClick={() => setType(val)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs transition-colors ${
                  type === val ? "bg-primary/15 text-primary" : "text-foreground/45 hover:text-foreground/70"
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          <textarea
            ref={taRef}
            data-testid="campfire-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            maxLength={600}
            placeholder={t("atelier.campfirePlaceholder")}
            className="w-full bg-transparent resize-none outline-none text-foreground placeholder:text-foreground/35 leading-relaxed"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={post}
              disabled={posting}
              data-testid="campfire-post-btn"
              className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60"
            >
              {posting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {t("atelier.campfirePost")}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary/60" size={26} /></div>
        ) : posts.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center">
            <Flame className="mx-auto text-primary/50 mb-4" size={30} />
            <p className="font-display italic text-foreground/50 text-xl">{t("atelier.campfireEmpty")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence initial={false}>
              {posts.map((p) => (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  data-testid="campfire-post"
                  className="glass frame-pulse rounded-2xl p-5"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-foreground/85">{p.user_name}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        p.type === "question" ? "bg-primary/10 text-primary/80" : "bg-primary/15 text-primary"
                      }`}>
                        {p.type === "question" ? t("atelier.campfireQuestion") : t("atelier.campfireEpiphany")}
                      </span>
                    </div>
                    <span className="text-xs text-foreground/30">{formatDate(p.created_at, i18n.language)}</span>
                  </div>
                  <p className="font-display italic text-lg text-foreground/80 leading-relaxed mb-4 whitespace-pre-line">{p.content}</p>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => echo(p.id)}
                      data-testid={`campfire-echo-${p.id}`}
                      className={`inline-flex items-center gap-1.5 text-sm transition-colors ${
                        p.echoed ? "text-primary" : "text-foreground/45 hover:text-primary"
                      }`}
                    >
                      <Flame size={15} className={p.echoed ? "fill-primary/30" : ""} /> {t("atelier.campfireEcho")} {p.echoes > 0 ? p.echoes : ""}
                    </button>
                    {p.mine && (
                      <button
                        onClick={() => remove(p.id)}
                        data-testid={`campfire-delete-${p.id}`}
                        className="inline-flex items-center gap-1.5 text-sm text-foreground/35 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
